import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, MenuItem } from "@mui/material";
import { format } from "date-fns";
import { CircleUser, ArrowLeft } from "lucide-react";
import { openSnackAlert } from "../../helper";
import AppLoader from "../../components/appLoader";
import CustomSelect from "../../components/customSelect";
import DynamicTable from "../../components/dynamicTable";
import { getAdjustmentHistory } from "../../services/Attendance";
import { getAllWorkers } from "../../services/Workers";

const DATA_FIELDS = [
  { key: "type", label: "Type" },
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
  { key: "status", label: "Status" },
];

/**
 * Adjustment History Page
 * URL: /adjustment-history
 * Shows all attendance adjustment history with optional worker filter
 */
const AttendanceAdjustmentHistory = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("all");
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadWorkers = async () => {
      try {
        const response = await getAllWorkers();
        if (!cancelled && (response?.status === 200 || response?.status === 201)) {
          const workersList = response?.data?.data?.workers || response?.data?.workers || [];
          setWorkers(workersList);
        }
      } catch (error) {
        if (!cancelled) openSnackAlert("Error loading workers", "error");
      }
    };
    loadWorkers();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const employeeId = selectedWorker && selectedWorker !== "all" ? selectedWorker : null;
    loadHistory(currentPage + 1, rowsPerPage, employeeId);
  }, [currentPage, rowsPerPage, selectedWorker]);

  const loadHistory = async (page = 1, limit = 10, employeeId = null) => {
    setLoading(true);
    try {
      const response = await getAdjustmentHistory({
        page,
        limit,
        ...(employeeId ? { employeeId } : {}),
      });
      if (response?.status === 200 || response?.status === 201) {
        const payload = response?.data?.data || response?.data || {};
        setHistoryData(payload?.data || []);
        setTotalRecords(payload?.total || 0);
      }
    } catch (error) {
      openSnackAlert(error?.message || "Failed to load history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleWorkerChange = (event) => {
    if (event && event.target) {
      setSelectedWorker(event.target.value);
    } else if (typeof event === "string") {
      setSelectedWorker(event);
    } else {
      setSelectedWorker("all");
    }
    setCurrentPage(0);
  };

  const handleChangePage = (_event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  // compareWith: old data, changed fields get highlighted
  const formatDataObject = (data, compareWith = null) => {
    if (!data) return "-";
    return (
      <Box>
        {DATA_FIELDS.map(({ key, label }) => {
          const changed = compareWith && data[key] !== compareWith[key];
          return (
            <Typography key={key} sx={{ fontSize: 12 }}>
              <Box component="span" sx={{ color: "text.secondary" }}>
                {label}:{" "}
              </Box>
              <Box
                component="span"
                sx={changed ? { fontWeight: 700, color: "primary.main" } : undefined}
              >
                {data[key] || "-"}
              </Box>
            </Typography>
          );
        })}
      </Box>
    );
  };

  const formatEmployee = (employee) => {
    if (!employee) return "-";
    const name = `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
    const empId = employee.employeeId ? ` (${employee.employeeId})` : "";
    return name ? `${name}${empId}` : empId || "-";
  };

const tableHeader = [
  { id: "employeeName", title: "Employee", align: "left" },
  // { id: "action", title: "Action", align: "center" },
  { id: "oldData", title: "Before", align: "left" },
  { id: "newData", title: "After", align: "left" },
  { id: "note", title: "Note", align: "left" },
  { id: "createdAt", title: "Date & Time", align: "center" },
];

  const displayRows = [
    "employeeName",
    // "action",
    "oldData",
    "newData",
    "note",
    "createdAt",
  ];

  const tableData = historyData.map((item) => ({
    ...item,
    employeeName: formatEmployee(item.employeeId),
    action:
      item.action === "create"
        ? "Create"
        : item.action === "update"
        ? "Update"
        : "Delete",
    oldData: formatDataObject(item.oldData),
    newData: formatDataObject(item.newData, item.oldData),
    note: item.note || item.newData?.notes || "-",
    createdAt: item.createdAt
      ? format(new Date(item.createdAt), "dd/MM/yyyy HH:mm")
      : "-",
  }));

  return (
    <Box bgcolor="primary.bgLight" padding={"20px"} borderRadius={"22px"} mt={0}>
      <AppLoader isVisible={loading} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box
            onClick={() => navigate(-1)}
            display="flex"
            alignItems="center"
            gap={0.5}
            mb={1}
            sx={{ cursor: "pointer", width: "fit-content" }}
          >
            <ArrowLeft size={16} />
            <Typography variant="body2">Back</Typography>
          </Box>
          <Typography variant="h4">Adjustment History</Typography>
          <Typography variant="description">
            View all attendance adjustment records across workers
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="body1" gutterBottom>
            Filter by Worker
          </Typography>
          <CustomSelect
            name="worker"
            placeholder="All Workers"
            value={selectedWorker}
            onChange={handleWorkerChange}
            icon={<CircleUser size={16} />}
            sx={{ width: "100%", height: "36px" }}
          >
            <MenuItem value="all">All Workers</MenuItem>
            {workers?.map((worker) => (
              <MenuItem key={worker._id} value={worker._id}>
                {worker.firstName} {worker.lastName} ({worker.employeeId})
              </MenuItem>
            ))}
          </CustomSelect>
        </Grid>

        <Grid item xs={12}>
          <DynamicTable
            tableHeader={tableHeader}
            tableData={tableData}
            displayRows={displayRows}
            showPagination={true}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            totalRecords={totalRecords}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AttendanceAdjustmentHistory;